// File: components/RecommendedActionsSection.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomCard } from '@/modules/Dashboard/DashboardPage/Components/MetricComponents';

type EnrichmentHint = 'none' | 'deterministic_only' | 'ai_enriched';

interface RecommendedActionProps {
  actions: string[] | undefined;
  riskScore: number;
  onActionClick?: (action: string) => void;
  /** Explains whether copy is rules-only or may include optional AI text (reasons on v2 actions). */
  enrichmentHint?: EnrichmentHint;
}

const RecommendedActionsSection: React.FC<RecommendedActionProps> = ({
  actions,
  riskScore,
  onActionClick,
  enrichmentHint = 'none',
}) => {
  const { t } = useTranslation();

  // If no actions are provided
  if (!Array.isArray(actions) || actions.length === 0) {
    return (
      <CustomCard
        title={t('MemberProfilePage.recommendedActionsLabel', 'Recommended Actions')}
        className="flex h-full min-h-0 w-full flex-1 flex-col"
      >
        <div className="flex flex-1 flex-col items-center justify-center px-1 py-6 text-center min-h-0">
          <span className="mb-4 text-xs px-2 py-1 rounded-lg bg-custom-blue text-white font-semibold">
            {t('MemberProfilePage.noActionsLabel', 'No action needed')}
          </span>
          <p className="text-custom-darkSilver text-xs">
            {t(
              'MemberProfilePage.noActionsMessage' as string,
              "This member currently has a low churn risk and doesn't require intervention." as string,
            )}
          </p>
        </div>
      </CustomCard>
    );
  }

  // Get action icon based on action text content
  const getActionIcon = (action: string) => {
    if (action.toLowerCase().includes('email') || action.toLowerCase().includes('message')) {
      return (
        <svg className="w-4 h-4 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
        </svg>
      );
    } else if (
      action.toLowerCase().includes('discount') ||
      action.toLowerCase().includes('offer') ||
      action.toLowerCase().includes('price')
    ) {
      return (
        <svg className="w-4 h-4 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.790l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z"
            clipRule="evenodd"
          ></path>
        </svg>
      );
    } else if (action.toLowerCase().includes('call') || action.toLowerCase().includes('phone')) {
      return (
        <svg className="w-4 h-4 text-purple-300" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z"
            clipRule="evenodd"
          ></path>
        </svg>
      );
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col rounded-lg border border-custom-lightSilver bg-custom-silver p-4">
      <h3 className="mb-1 text-md font-semibold text-custom-black">
        {t('MemberProfilePage.recommendedActionsLabel', 'Recommended Actions')}
      </h3>
      {enrichmentHint === 'deterministic_only' && (
        <p className="text-custom-darkSilver text-xs leading-snug mb-2">
          {t(
            'MemberProfilePage.recommendedActionsDeterministicHint',
            'Suggestions come from ChurnSignal rules. Optional AI-generated explanations may appear later when enabled for your workspace.',
          )}
        </p>
      )}
      {enrichmentHint === 'ai_enriched' && (
        <p className="text-custom-darkSilver text-xs leading-snug mb-2">
          {t(
            'MemberProfilePage.recommendedActionsAiHint',
            'Where shown, short explanations may be AI-assisted and are grounded only on metrics already visible in your account.',
          )}
        </p>
      )}
      <ul className="mt-4 min-h-0 flex-1 overflow-y-auto">
        {actions.slice(0, 3).map((action, index) => (
          <li key={index}>
            <button
              className="w-full mb-2 flex items-center text-left group"
              onClick={() => onActionClick && onActionClick(action)}
            >
              <span className="shrink-0 mr-2">{getActionIcon(action)}</span>
              <span className="text-sm text-custom-darkSilver transition-colors group-hover:text-custom-black">
                {action}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RecommendedActionsSection;
